import type { Bi } from 'schools/numerology/types/bilingual';

export type NumerologyTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type NumerologyChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: NumerologyTopic[];
};

export const NUMEROLOGY_HERO = {
  eyebrow: {
    en: 'Chaldean system · Ankotish',
    hi: 'चाल्डियन / शॉल्डियन प्रणाली · अंकोतिष',
  },
  titleShort: { en: 'Chaldean Numerology', hi: 'चाल्डियन / शॉल्डियन अंक ज्योतिष' },
  title: { en: 'Chaldean Numerology', hi: 'चाल्डियन / शॉल्डियन अंक ज्योतिष' },
  motto: {
    en: 'Sound-based letter chart, compound numbers, and prescriptive name–birth reading.',
    hi: 'ध्वनि-आधारित अक्षर चार्ट, संयुक्त अंक, और निर्देशात्मक नाम–जन्म पठन।',
  },
  tagline: {
    en: 'Fifty-one step-by-step lessons: Mulank, Bhagyank, compounds 10–52, name analysis, compatibility, correction, and professional practice.',
    hi: 'इक्यावन चरणबद्ध पाठ: मूलांक, भाग्यांक, संयुक्त 10–52, नाम विश्लेषण, अनुकूलता, सुधार और व्यावसायिक अभ्यास।',
  },
  intro: {
    en: 'Learn Chaldean numerology the way practitioners use it in India-calculate first, interpret second, never mix with Pythagorean letter values.',
    hi: 'भारत में अभ्यासी जैसे चाल्डियन / शॉल्डियन अंक ज्योतिष सीखें-पहले गणना, फिर व्याख्या; पाइथागोरस अक्षर-मान कभी न मिलाएँ।',
  },
  trait: {
    en: 'Chaldean chart · compound before reduce',
    hi: 'चाल्डियन / शॉल्डियन चार्ट · घटाने से पहले संयुक्त',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): NumerologyTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const NUMEROLOGY_CHAPTERS: NumerologyChapter[] = [
  {
    id: 'introduction',
    number: 1,
    title: { en: 'Introduction', hi: 'परिचय' },
    subtitle: {
      en: 'What numerology is, where Chaldean practice comes from, and the three rules you never break',
      hi: 'अंक ज्योतिष क्या है, चाल्डियन / शॉल्डियन कहाँ से आया, और तीन अटूट नियम',
    },
    icon: '🔢',
    topics: [
      topic(
        'what-is-numerology',
        'What Is Numerology?',
        'अंक ज्योतिष क्या है?',
        'Define scope: name + date vibrations, limits vs Jyotisha, ethical frame.',
        'क्षेत्र: नाम + तिथि कंपन, ज्योतिष से सीमा, नैतिक ढाँचा।',
        10,
        '🔢',
      ),
      topic(
        'chaldean-origins',
        'Origins of Chaldean Numerology',
        'चाल्डियन / शॉल्डियन अंक ज्योतिष की उत्पत्ति',
        'Babylon sound chart, sacred 9, Cheiro lineage in modern India.',
        'बेबीलोन ध्वनि चार्ट, पवित्र 9, भारत में चेइरो परंपरा।',
        11,
        '🏛️',
      ),
      topic(
        'core-principles',
        'Core Principles',
        'मूल सिद्धांत',
        'Chaldean chart only, compound then single, daily-use name rule.',
        'केवल चाल्डियन / शॉल्डियन चार्ट, पहले संयुक्त फिर एकल, दैनिक नाम नियम।',
        12,
        '💡',
      ),
    ],
  },
  {
    id: 'chaldean-system',
    number: 2,
    title: { en: 'Chaldean Number System', hi: 'चाल्डियन / शॉल्डियन संख्या प्रणाली' },
    subtitle: {
      en: 'Letter chart, name arithmetic, compound handling, and timed drills',
      hi: 'अक्षर चार्ट, नाम गणित, संयुक्त अंक, और समयबद्ध अभ्यास',
    },
    icon: '📊',
    topics: [
      topic(
        'chaldean-letter-chart',
        'The Chaldean Number Chart',
        'चाल्डियन / शॉल्डियन संख्या चार्ट',
        'Full A–Z mapping; no letter equals 9; transliteration basics.',
        'पूर्ण A–Z मान; कोई अक्षर 9 नहीं; लिप्यंतरण आधार।',
        12,
        '📊',
      ),
      topic(
        'calculating-name-numbers',
        'Calculating Name Numbers',
        'नाम अंक की गणना',
        'Raw total, compound, reduction chain, spelling edge cases.',
        'कच्चा योग, संयुक्त, घटाव श्रृंखला, वर्तनी विशेष।',
        14,
        '✍️',
      ),
      topic(
        'compound-vs-single',
        'Compound vs Single Numbers',
        'संयुक्त बनाम एकल अंक',
        'When to stop reducing; master 11 and 22; read order.',
        'कब रुकें; 11 और 22; पढ़ने का क्रम।',
        13,
        '🔀',
      ),
      topic(
        'chaldean-practice-drills',
        'Practical Exercises',
        'व्यावहारिक अभ्यास',
        'Ten-name drill, initials, hyphens, timed worksheet.',
        'दस नाम अभ्यास, आद्याक्षर, हाइफ़न, समयबद्ध शीट।',
        15,
        '📝',
      ),
    ],
  },
  {
    id: 'birth-numbers',
    number: 3,
    title: { en: 'Birth Numbers', hi: 'जन्म अंक' },
    subtitle: {
      en: 'Mulank (Driver), Bhagyank (Conductor), and how they work together',
      hi: 'मूलांक (ड्राइवर), भाग्यांक (कंडक्टर), और उनका संबंध',
    },
    icon: '🎂',
    topics: [
      topic(
        'mulank-birth-number',
        'Birth Number (Mulank)',
        'मूलांक (जन्म अंक)',
        'Day-of-month only; 1–31 handling; personality core.',
        'केवल जन्म दिन; 1–31; व्यक्तित्व कोर।',
        12,
        '🎂',
      ),
      topic(
        'bhagyank-destiny-number',
        'Destiny Number (Bhagyank)',
        'भाग्यांक (भाग्य अंक)',
        'Full DOB sum and reduction; life-path direction.',
        'पूर्ण जन्म तिथि योग और घटाव; जीवन दिशा।',
        12,
        '🌟',
      ),
      topic(
        'birth-destiny-relationship',
        'Birth and Destiny Relationship',
        'मूलांक और भाग्यांक का संबंध',
        'Driver vs Conductor harmony, tension, case patterns.',
        'ड्राइवर बनाम कंडक्टर सामंजस्य, तनाव, उदाहरण।',
        11,
        '🔗',
      ),
      topic(
        'birth-numbers-workshop',
        'Practice Workshop',
        'अभ्यास कार्यशाला',
        'Five full birth profiles in fifteen minutes.',
        'पंद्रह मिनट में पाँच पूर्ण जन्म प्रोफ़ाइल।',
        14,
        '🛠️',
      ),
    ],
  },
  {
    id: 'number-meanings',
    number: 4,
    title: { en: 'Meaning of Numbers 1–9', hi: 'अंक 1–9 का अर्थ' },
    subtitle: {
      en: 'Graha-linked archetypes, strengths, shadows, and the friendship matrix',
      hi: 'ग्रह-जुड़े स्वरूप, ताकत, छाया, और मित्रता मैट्रिक्स',
    },
    icon: '1️⃣',
    topics: [
      topic('numbers-1-and-2', 'Numbers 1 & 2', 'अंक 1 और 2', 'Sun and Moon temperaments.', 'सूर्य और चंद्र स्वभाव।', 17, '1️⃣'),
      topic('numbers-3-and-4', 'Numbers 3 & 4', 'अंक 3 और 4', 'Jupiter creativity vs Rahu structure.', 'गुरु रचनात्मकता बनाम राहु संरचना।', 17, '3️⃣'),
      topic('numbers-5-and-6', 'Numbers 5 & 6', 'अंक 5 और 6', 'Mercury change vs Venus duty.', 'बुध परिवर्तन बनाम शुक्र कर्तव्य।', 17, '5️⃣'),
      topic('numbers-7-and-8', 'Numbers 7 & 8', 'अंक 7 और 8', 'Ketu seeker vs Saturn karma worker.', 'केतु साधक बनाम शनि कर्मकार।', 17, '7️⃣'),
      topic('number-9', 'Number 9', 'अंक 9', 'Mars completion; material caution on Bhagyank 9.', 'मंगल पूर्णता; भाग्यांक 9 पर भौतिक सावधानी।', 16, '9️⃣'),
      topic(
        'number-comparison-matrix',
        'Comparison of All Numbers',
        'सभी अंकों की तुलना',
        'Friend, enemy, neutral grid for readings.',
        'पठन हेतु मित्र, शत्रु, तटस्थ ग्रिड।',
        13,
        '📋',
      ),
    ],
  },
  {
    id: 'compound-numbers',
    number: 5,
    title: { en: 'Compound Numbers', hi: 'संयुक्त अंक' },
    subtitle: {
      en: 'Read 10–52 before reducing; lucky, karmic, and case-led interpretation',
      hi: 'घटाने से पहले 10–52 पढ़ें; शुभ, कर्मिक, केस-आधारित व्याख्या',
    },
    icon: '🔢',
    topics: [
      topic('what-are-compound-numbers', 'What Are Compound Numbers?', 'संयुक्त अंक क्या हैं?', 'Range, tone categories, reference method.', 'सीमा, श्रेणी, संदर्भ विधि।', 18, '🔢'),
      topic('lucky-compound-numbers', 'Lucky Compound Numbers', 'शुभ संयुक्त अंक', '10, 15, 17, 19, 21 and selection use.', '10, 15, 17, 19, 21 और चयन।', 19, '🍀'),
      topic('challenging-compound-numbers', 'Challenging Compound Numbers', 'चुनौतीपूर्ण संयुक्त अंक', '13, 14, 16, 18 growth framing.', '13, 14, 16, 18 विकास ढाँचा।', 19, '⚠️'),
      topic('compound-hidden-meanings', 'Hidden Meanings and Symbolism', 'छिपे अर्थ और प्रतीक', '11 vs 2; when not to reduce.', '11 बनाम 2; कब न घटाएँ।', 18, '🔮'),
      topic('compound-case-studies', 'Case Studies', 'केस स्टडी', 'Three real-style name totals with compounds.', 'तीन वास्तविक शैली के नाम योग।', 20, '📖'),
    ],
  },
  {
    id: 'name-analysis',
    number: 6,
    title: { en: 'Name Analysis', hi: 'नाम विश्लेषण' },
    subtitle: {
      en: 'First name, full name, business and brand naming reads',
      hi: 'पहला नाम, पूरा नाम, व्यापार और ब्रांड नाम पठन',
    },
    icon: '📛',
    topics: [
      topic('reading-name-number', 'Reading a Name Number', 'नाम अंक पढ़ना', 'Expression total and first impression.', 'अभिव्यक्ति योग और पहली छाप।', 17, '📛'),
      topic('first-name-analysis', 'First Name Analysis', 'पहला नाम विश्लेषण', 'Calling name vs legal name.', 'बुलाने वाला नाम बनाम कानूनी।', 18, '👤'),
      topic('full-name-analysis', 'Full Name Analysis', 'पूरा नाम विश्लेषण', 'First, middle, surname layers.', 'पहला, मध्य, उपनाम स्तर।', 19, '📜'),
      topic('business-name-analysis', 'Business Name Analysis', 'व्यापार नाम विश्लेषण', 'Trade vs registered; industry fit.', 'व्यापार बनाम पंजीकृत; उद्योग मेल।', 18, '🏢'),
      topic('brand-name-analysis', 'Brand Name Analysis', 'ब्रांड नाम विश्लेषण', 'Memorability + compound harmony.', 'यादगार + संयुक्त सामंजस्य।', 18, '✨'),
    ],
  },
  {
    id: 'compatibility',
    number: 7,
    title: { en: 'Compatibility Analysis', hi: 'अनुकूलता विश्लेषण' },
    subtitle: {
      en: 'Friendship matrix applied to marriage, business, and teams',
      hi: 'विवाह, व्यापार और टीम पर मित्रता मैट्रिक्स',
    },
    icon: '🤝',
    topics: [
      topic('number-friendships', 'Number Friendships', 'अंक मित्रता', 'Natural friends, enemies, neutrals.', 'स्वाभाविक मित्र, शत्रु, तटस्थ।', 17, '🤝'),
      topic('marriage-compatibility', 'Marriage Compatibility', 'विवाह अनुकूलता', 'Mulank + Bhagyank + name cross-check.', 'मूलांक + भाग्यांक + नाम जाँच।', 19, '💍'),
      topic('business-partnerships', 'Business Partnerships', 'व्यापार साझेदारी', 'Complementary drivers in co-founders.', 'सह-संस्थापकों में पूरक ड्राइवर।', 18, '🤜🤛'),
      topic('team-dynamics', 'Team Dynamics', 'टीम गतिशीलता', 'Group missing-digit audit.', 'समूह लुप्त-अंक ऑडिट।', 18, '👥'),
    ],
  },
  {
    id: 'advanced',
    number: 8,
    title: { en: 'Advanced Chaldean Numerology', hi: 'उन्नत चाल्डियन / शॉल्डियन अंक ज्योतिष' },
    subtitle: {
      en: 'Missing and repeating digits, synthesis, karmic debts, spiritual numbers',
      hi: 'लुप्त और दोहराए अंक, संश्लेषण, कर्म ऋण, आध्यात्मिक अंक',
    },
    icon: '🔮',
    topics: [
      topic('missing-numbers', 'Missing Numbers', 'लुप्त अंक', 'Gaps from DOB; development vs acceptance.', 'जन्म तिथि से अंतर; विकास बनाम स्वीकार।', 18, '⬜'),
      topic('repeating-numbers', 'Repeating Numbers', 'दोहराए अंक', 'Intensity vs obsession in DOB/name.', 'जन्म/नाम में तीव्रता बनाम आसक्ति।', 17, '🔁'),
      topic('hidden-strengths-weaknesses', 'Hidden Strengths and Weaknesses', 'छिपी शक्तियाँ और कमज़ोरियाँ', 'One-page strength map synthesis.', 'एक पृष्ठ शक्ति मानचित्र।', 19, '💪'),
      topic('karmic-debts', 'Karmic Debts', 'कर्म ऋण', '13, 14, 16, 19 ethical delivery.', '13, 14, 16, 19 नैतिक भाषा।', 19, '⚖️'),
      topic('spiritual-numbers', 'Spiritual Numbers', 'आध्यात्मिक अंक', '7, 9, 11, 22 spiritual vs escapist.', '7, 9, 11, 22 आध्यात्मिक बनाम पलायन।', 17, '🕉️'),
    ],
  },
  {
    id: 'name-correction',
    number: 9,
    title: { en: 'Name Correction & Remedies', hi: 'नाम सुधार और उपाय' },
    subtitle: {
      en: 'When to suggest change, minimal edits, favorable totals, ethics',
      hi: 'कब बदलाव, न्यूनतम संशोधन, अनुकूल योग, नैतिकता',
    },
    icon: '✏️',
    topics: [
      topic('why-name-changes', 'Why Name Changes Are Suggested', 'नाम परिवर्तन क्यों सुझाया जाता है', 'Decision checklist before any edit.', 'संशोधन से पहले चेकलिस्ट।', 17, '✏️'),
      topic('name-correction-principles', 'Principles of Name Correction', 'नाम सुधार के सिद्धांत', 'Minimal spelling change rules.', 'न्यूनतम वर्तनी नियम।', 18, '📐'),
      topic('selecting-favorable-numbers', 'Selecting Favorable Numbers', 'अनुकूल अंक चुनना', 'Align to Mulank/Bhagyank; avoid enemies.', 'मूलांक/भाग्यांक से मेल; शत्रु से बचें।', 19, '🎯'),
      topic('name-correction-cases', 'Case Studies', 'केस स्टडी', 'Before/after proposals peer-reviewed.', 'पहले/बाद प्रस्ताव समीक्षा।', 20, '📖'),
      topic('ethical-guidelines', 'Ethical Guidelines', 'नैतिक दिशानिर्देश', 'No guarantees; mental health boundaries.', 'कोई गारंटी नहीं; मानसिक स्वास्थ्य सीमा।', 18, '🙏'),
    ],
  },
  {
    id: 'professional',
    number: 10,
    title: { en: 'Professional Practice', hi: 'व्यावसायिक अंक ज्योतिष' },
    subtitle: {
      en: 'Intake, consultation flow, report structure, Jyotisha crossover',
      hi: 'इनटेक, परामर्श प्रवाह, रिपोर्ट संरचना, ज्योतिष संयोजन',
    },
    icon: '🗣️',
    topics: [
      topic('conducting-consultation', 'Conducting a Consultation', 'परामर्श कैसे करें', '30-minute mock session structure.', '30 मिनट मॉक सत्र संरचना।', 19, '🗣️'),
      topic('client-data-collection', 'Client Data Collection', 'ग्राहक डेटा संग्रह', 'Standard intake form fields.', 'मानक इनटेक फ़ॉर्म।', 17, '📋'),
      topic('report-writing', 'Report Writing', 'रिपोर्ट लेखन', 'Section order for PDF deliverable.', 'PDF अनुभाग क्रम।', 20, '📄'),
      topic('numerology-with-jyotisha', 'Combining Numerology with Astrology', 'अंक ज्योतिष + ज्योतिष', 'When to cross-check chart.', 'कब कुंडली जाँचें।', 19, '🪐'),
      topic('professional-case-studies', 'Real-Life Case Studies', 'वास्तविक केस स्टडी', 'Three anonymized full reads.', 'तीन गुमनाम पूर्ण पठन।', 22, '🌍'),
    ],
  },
  {
    id: 'vedic-bridge',
    number: 11,
    title: { en: 'Vedic Bridge', hi: 'वैदिक कड़ी' },
    subtitle: {
      en: 'Graha overlay, light nakshatra touch, dasha + personal year, integrated read',
      hi: 'ग्रह परत, नक्षत्र संकेत, दशा + व्यक्तिगत वर्ष, एकीकृत पठन',
    },
    icon: '🪐',
    topics: [
      topic('mulank-graha-bridge', 'Birth Number and Mulank', 'मूलांक और ग्रह मूलांक', 'Same math, Jyotisha framing.', 'वही गणित, ज्योतिष ढाँचा।', 17, '☀️'),
      topic('numbers-planetary-connections', 'Numbers and Planets', 'अंक और ग्रह संबंध', '1–9 navagraha table (single source).', '1–9 नवग्रह सारणी।', 18, '🪐'),
      topic('numerology-nakshatras', 'Numerology with Nakshatras', 'नक्षत्रों के साथ अंक ज्योतिष', 'Light tone overlay example.', 'हल्की स्वर परत उदाहरण।', 17, '⭐'),
      topic('numerology-dasha-timing', 'Numerology + Dasha', 'अंक ज्योतिष + दशा', 'Personal year + Vimshottari windows.', 'व्यक्तिगत वर्ष + विंशोत्तरी खिड़की।', 19, '⏳'),
      topic('integrated-reading', 'Complete Integrated Reading', 'संपूर्ण एकीकृत रीडिंग', 'Capstone: birth + name + timing.', 'कैपस्टोन: जन्म + नाम + समय।', 22, '🧮'),
    ],
  },
];

const topicById = new Map<string, { topic: NumerologyTopic; chapter: NumerologyChapter }>();

for (const chapter of NUMEROLOGY_CHAPTERS) {
  for (const t of chapter.topics) {
    topicById.set(t.id, { topic: t, chapter });
  }
}

export function getNumerologyTopic(id: string) {
  return topicById.get(id);
}

export function getNumerologyChapter(chapterId: string): NumerologyChapter | undefined {
  return NUMEROLOGY_CHAPTERS.find((c) => c.id === chapterId);
}

export function getNumerologyChapterIndex(chapterId: string): number {
  return NUMEROLOGY_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getNumerologyTopicCount(): number {
  return NUMEROLOGY_CHAPTERS.reduce((sum, c) => sum + c.topics.length, 0);
}

const flatTopicOrder = NUMEROLOGY_CHAPTERS.flatMap((c) => c.topics);

export function getNumerologyTopicNeighbors(topicId: string): {
  prev: NumerologyTopic | null;
  next: NumerologyTopic | null;
} {
  const idx = flatTopicOrder.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? flatTopicOrder[idx - 1] : null,
    next: idx < flatTopicOrder.length - 1 ? flatTopicOrder[idx + 1] : null,
  };
}

/** Search index terms for AskGuru and global search. */
export function getNumerologySearchTerms(): Array<{ id: string; terms: string }> {
  return flatTopicOrder.map((t) => {
    const ch = topicById.get(t.id)?.chapter;
    return {
      id: t.id,
      terms: [
        t.id,
        t.title.en,
        t.title.hi,
        t.summary.en,
        t.summary.hi,
        ch?.title.en,
        ch?.title.hi,
        'numerology',
        'chaldean',
        'ankotish',
        'mulank',
        'bhagyank',
      ].join(' '),
    };
  });
}
